import { useEffect, useState } from "react";
import { Button } from "../../../components/Button";
import { FCRReport } from "../FCRReport";
import { Tabs } from "./Tabs";
import { Performance } from "./report-tabs/Performance";
import { SessionRecap } from "./report-tabs/SessionRecap";
import { BackButton } from "../../../components/Button/BackButton";
import { useReports } from "../../praxis-play-dashboard/context/reports-context";
import { useAnalytics } from "../../../integrations/posthog/useAnalytics";
import { PRAXIS_PLAY_EVENTS } from "../../../integrations/posthog/events";
import { useTutorial } from "../../../contexts/TutorialContext";
import { TutorialTooltip } from "../../../components/TutorialTooltip";
import { FeedbackView } from "../../praxis-play/components/FeedbackView";
import type { ReportSummaryItem } from "../../../types/report";

export type Conversation = {
  id: string;
  message: string;
  isUserMessage: boolean;
  session_url?: string | null;
  audio_file?: any;
};

type Report = {
  conversations: Conversation[];
  languageClarity: number;
  vocabulary: number;
  brandStrategy: number;
  questionTechniques: number;
  compliancePercentage: number;
  tone: number;
  duration?: number;
  report: {
    variable_metrics: {
      [key: string]: {
        value: number;
        feedback: string;
      };
    };
    constant_metrics: {
      [key: string]: {
        value: number;
        feedback: string;
      };
    };
    reportScore?: number;
    summary?: string | ReportSummaryItem[];
    coaching_matrix?: any;
  };
  session: {
    sessionID: string;
    userId: string;
  };
};

export function ReportView({
  report,
  detailedReport,
  setCallStatus,
  isReport = false,
  onBackClick,
  detailedReportFetching,
  isFromRoleplay = false,
  ifFromDashboard = false,
  showTutorial = false,
  showCompactFeedback = false,
}: {
  report: Report;
  detailedReport: any;
  setCallStatus?: React.Dispatch<React.SetStateAction<string>>;
  isReport?: boolean;
  onBackClick?: () => void;
  detailedReportFetching?: boolean;
  isFromRoleplay?: boolean;
  ifFromDashboard?: boolean;
  /** When true, show tutorial tooltips (e.g. when viewing report from call history) */
  showTutorial?: boolean;
  showCompactFeedback?: boolean;
}) {
  const { captureEvent } = useAnalytics();
  const [tab, setTab] = useState("performance");
  const [showFcrReport, setShowFcrReport] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number | undefined>(
    undefined
  );
  const { reportAverage } = useReports();
  
  // Tutorial support: show on any PraxisPlay report view (after call, dashboard link, or call history)
  const { nextStep, skip, getCurrentStepConfig, isVisible } = useTutorial();
  const currentStep = getCurrentStepConfig();
  const isPraxisPlay = isFromRoleplay || ifFromDashboard || showTutorial;
  const shouldShowTutorial = isPraxisPlay && isVisible;

  const variableMetrics = report?.report?.variable_metrics || {};
  const constantMetrics = report?.report?.constant_metrics || {};

  const metrics = { ...variableMetrics, ...constantMetrics };

  useEffect(() => {
    captureEvent(PRAXIS_PLAY_EVENTS.FEEDBACK_VIEWED, {
      session_id: report?.session?.sessionID,
      is_from_roleplay: isFromRoleplay,
      source: isFromRoleplay ? "post_training" : ifFromDashboard ? "dashboard_link" : "history_navigation",
    });
  }, [report]);

  useEffect(() => {
    if (tab === "performance") {
      captureEvent(PRAXIS_PLAY_EVENTS.FEEDBACK_PERFORMANCE_TAB_ACCESSED, {
        tab_index: 0,
        tab_name: "performance",
        session_id: report?.session?.sessionID,
      });
    } else if (tab === "session-recap") {
      captureEvent(PRAXIS_PLAY_EVENTS.FEEDBACK_SESSION_RECAP_TAB_ACCESSED, {
        tab_index: 1,
        tab_name: "session_recap",
        session_id: report?.session?.sessionID,
      });
    }
  }, [tab]);

  return (
    <div data-testid="session-report" className="bg-background p-4 pt-20 lg:p-6">
      {!showFcrReport ? (
        <>
          <div className="md:mb-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                {isReport && (
                  <BackButton
                    onClick={() => {
                      if (onBackClick) {
                        onBackClick?.();
                      } else {
                        window.history.back();
                      }
                    }}
                  />
                )}
                <div className="text-text-primary text-2xl font-bold">
                  PraxisPlay Insights
                </div>
              </div>
              <div className="text-text-primary text-lg mt-3">
                View your AI generated feedback for your last PraxisPlay session.
              </div>
            </div>
            {!isReport && (
              <Button
                variant="primary"
                className="hidden md:block !rounded-full !py-3 !px-10"
                onClick={() => setCallStatus?.("idle")}
              >
                New Session
              </Button>
            )}
          </div>
          {!isReport && (
            <Button
              variant="primary"
              className="md:hidden my-5 w-full !rounded-full !py-3 !px-10"
              onClick={() => setCallStatus?.("idle")}
            >
              New Session
            </Button>
          )}
          <div className="mb-5">
            <div className="relative w-fit border-b-2 border-[#D5D5D5] my-4">
              <Tabs value={tab} onChange={setTab} detailedReportFetching={detailedReportFetching || false} />
              {/* <div className="absolute bottom-2 right-0">
                <CallAudio
                  conversations={report?.conversations || []}
                  currentIndex={currentIndex}
                  setCurrentIndex={setCurrentIndex}
                />
              </div> */}
            </div>
            {tab === "performance" && (
              <Performance report={report} metrics={metrics} reportAverage={reportAverage} />
            )}
            {tab === "session-recap" && (
              <SessionRecap
                report={report}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                metrics={metrics}
                detailedReport={detailedReport}
              />
            )}
          </div>
        </>
      ) : (
        <FCRReport />
      )}
      {isPraxisPlay && (
        <TutorialTooltip
          step={currentStep}
          isVisible={shouldShowTutorial}
          onAction={nextStep}
          onSkip={skip}
        />
      )}
      <FeedbackView sessionId={(report as any)?.session?.sessionID} showCompactFeedback={showCompactFeedback} />
    </div>
  );
}
